import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from 'shared/utils/firebase';
import { buildMainUrl } from 'shared/utils/navigation';
import { Home, Code, BookOpen, Award, FileText, User, LogOut, ArrowRight, ExternalLink } from 'react-feather';

const UserProfileDropdown = ({ userDetails, styles, hasArrow = false }) => {
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setUserDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [userDropdownOpen]);

    const handleLogout = async () => {
        await signOut(auth);
        setUserDropdownOpen(false);
    };

    const avatarInitials = (() => {
        if (userDetails?.name) {
            return userDetails.name.charAt(0).toUpperCase();
        }
        if (userDetails?.email) {
            return userDetails.email.charAt(0).toUpperCase();
        }
        return 'U';
    })();

    return (
        <div className={styles.avatarWrapper} ref={dropdownRef}>
            <div
                className={styles.userAvatar}
                onClick={() => setUserDropdownOpen(prev => !prev)}
                title="Account"
            >
                {avatarInitials}
            </div>
            {userDropdownOpen && (
                <div className={styles.profileDropdown}>
                    <div className={styles.dropdownHeader}>
                        {styles.avatarMini && (
                            <div className={styles.avatarMini}>
                                {avatarInitials}
                            </div>
                        )}
                        <div className={styles.headerInfo || ''}>
                            {styles.dropdownName ? (
                                <>
                                    <span className={styles.dropdownName}>
                                        {userDetails?.name || `${userDetails?.first_name || ''} ${userDetails?.last_name || ''}`.trim() || 'User'}
                                    </span>
                                    <span className={styles.dropdownEmail}>{userDetails?.email}</span>
                                </>
                            ) : (
                                <>
                                    Logged in as:<br />
                                    <strong>{userDetails?.email || 'User'}</strong>
                                </>
                            )}
                        </div>
                    </div>

                    {styles.dropdownDivider && <div className={styles.dropdownDivider} />}

                    <div className={styles.dropdownItem} role="button" onClick={() => { setUserDropdownOpen(false); navigate('/page'); }}>
                        <Home size={14} className={styles.itemIcon || ''} style={!styles.itemIcon ? { marginRight: '8px' } : {}} />
                        <span>Home Page</span>
                        {hasArrow && styles.itemArrow && <ArrowRight size={12} className={styles.itemArrow} />}
                    </div>

                    {styles.dropdownDivider && <div className={styles.dropdownDivider} />}

                    <div className={styles.dropdownItem} role="button" onClick={() => { setUserDropdownOpen(false); navigate('/ide'); }}>
                        <Code size={14} className={styles.itemIcon || ''} style={!styles.itemIcon ? { marginRight: '8px' } : {}} />
                        <span>Codebox (IDE)</span>
                        {hasArrow && styles.itemArrow && <ArrowRight size={12} className={styles.itemArrow} />}
                    </div>

                    {styles.dropdownDivider && <div className={styles.dropdownDivider} />}

                    <div className={styles.dropdownItem} role="button" onClick={() => { setUserDropdownOpen(false); navigate('/exercise'); }}>
                        <BookOpen size={14} className={styles.itemIcon || ''} style={!styles.itemIcon ? { marginRight: '8px' } : {}} />
                        <span>Exercises</span>
                        {hasArrow && styles.itemArrow && <ArrowRight size={12} className={styles.itemArrow} />}
                    </div>

                    {styles.dropdownDivider && <div className={styles.dropdownDivider} />}

                    <div className={styles.dropdownItem} role="button" onClick={() => { setUserDropdownOpen(false); navigate('/challenges'); }}>
                        <Award size={14} className={styles.itemIcon || ''} style={!styles.itemIcon ? { marginRight: '8px' } : {}} />
                        <span>Challenges</span>
                        {hasArrow && styles.itemArrow && <ArrowRight size={12} className={styles.itemArrow} />}
                    </div>

                    {styles.dropdownDivider && <div className={styles.dropdownDivider} />}

                    <div className={styles.dropdownItem} role="button" onClick={() => { setUserDropdownOpen(false); navigate('/examples'); }}>
                        <FileText size={14} className={styles.itemIcon || ''} style={!styles.itemIcon ? { marginRight: '8px' } : {}} />
                        <span>Synced Examples</span>
                        {hasArrow && styles.itemArrow && <ArrowRight size={12} className={styles.itemArrow} />}
                    </div>

                    {styles.dropdownDivider && <div className={styles.dropdownDivider} />}

                    <div className={styles.dropdownItem} role="button" onClick={() => { setUserDropdownOpen(false); navigate('/profile'); }}>
                        <User size={14} className={styles.itemIcon || ''} style={!styles.itemIcon ? { marginRight: '8px' } : {}} />
                        <span>My Profile</span>
                        {hasArrow && styles.itemArrow && <ArrowRight size={12} className={styles.itemArrow} />}
                    </div>

                    {styles.dropdownDivider && <div className={styles.dropdownDivider} />}

                    <div className={styles.dropdownItem} role="button" onClick={() => { setUserDropdownOpen(false); window.location.href = buildMainUrl('/dashboard'); }}>
                        <ExternalLink size={14} className={styles.itemIcon || ''} style={!styles.itemIcon ? { marginRight: '8px' } : {}} />
                        <span>Main Portal</span>
                        {hasArrow && styles.itemArrow && <ArrowRight size={12} className={styles.itemArrow} />}
                    </div>

                    {styles.dropdownDivider && <div className={styles.dropdownDivider} />}

                    <div className={`${styles.dropdownItem} ${styles.dropdownLogout || ''}`} role="button" onClick={handleLogout}>
                        <LogOut size={14} className={styles.itemIcon || ''} style={!styles.itemIcon ? { marginRight: '8px' } : {}} />
                        <span>Log Out</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserProfileDropdown;
